import React, { useEffect } from 'react';
import { useSelector , useDispatch } from 'react-redux';
import { DEFAULT_THING_ID } from '@kemu-io/kemu-core/common/constants';
import useWidgetClipboardActions from '../LogicMapper/hooks/useWidgetClipboardActions';
import { selectSelectedWidgets, SelectedWidgetInfo, getSelectedWidgetIds } from '../LogicMapper/logicMapperSlice';
import { selectCurrentRecipe } from '../Workspace/workspaceSlice';
import { undoHistoryAction, redoHistoryAction } from '@src/app/reducers/history/historyActions';
import useIsMarketplaceVisible from '@common/hooks/useIsMarketplaceVisible';
import { STOP_SHORTCUT_PROPAGATION_CLASS } from '@common/constants';
import { showWidgetLauncher } from '@src/features/interface/interfaceSlice';

type Props = Record<string, never>;

/**
 * Mounts global keydown listeners for Copy, Cut, and Paste when rendered.
 * Defers to native browser behavior when focus is on an editable element.
 */
const GlobalShortcuts = (_props: Props): null => {
  const dispatch = useDispatch();
  const { copySelection, cutSelection, pasteFromClipboard, removeSelection, shouldIgnoreForEditable } = useWidgetClipboardActions();
  const recipeId = useSelector(selectCurrentRecipe).poolId;
  const marketplaceVisible = useIsMarketplaceVisible();
  const selectedWidgets = useSelector(selectSelectedWidgets);


  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const key = String(e.key || '').toLowerCase();
      const isMetaOrCtrl = e.metaKey || e.ctrlKey;
      const isDelete = key === 'backspace' || key === 'delete';

      if (!isMetaOrCtrl && !isDelete) { return; }
      if (e.altKey) { return; }

      if (shouldIgnoreForEditable(e.target)) { return; }
      if (marketplaceVisible) { return; }

      // Get actual widget IDs not virtual ports
      const widgetIds = getSelectedWidgetIds(selectedWidgets);
      const hasValidWidgets = widgetIds.length > 0;

      if(e.target && (e.target as HTMLElement).closest(`.${STOP_SHORTCUT_PROPAGATION_CLASS}`)) {
        return;
      }
      
      const isActionable = key === 'c' || key === 'x' || key === 'v' || isDelete;
      if (isActionable) {
        e.preventDefault();
      }


      if ((isMetaOrCtrl) && key === 'z' && !e.shiftKey) {
        dispatch(undoHistoryAction());
      } else if ((e.metaKey || e.ctrlKey) && key === 'z' && e.shiftKey) {
        dispatch(redoHistoryAction());
      } else if (key === 'c' && hasValidWidgets) {
        copySelection();
      } else if (key === 'x' && hasValidWidgets) {
        cutSelection();
      } else if (key === 'v') {
        pasteFromClipboard();
      } else if (isDelete && hasValidWidgets && recipeId) {
        removeSelection({
          recipeId,
          thingId: DEFAULT_THING_ID,
          widgetIds,
        });
      } else if (isMetaOrCtrl && key === 'a') {
        // Cmd/Ctrl + A: Open Widget Launcher (only when not in editable element)
        e.preventDefault();
        dispatch(showWidgetLauncher(true));
      }
    };

    if (!marketplaceVisible) {
      window.addEventListener('keydown', onKeyDown);
      return () => window.removeEventListener('keydown', onKeyDown);
    }

    return;
  }, [copySelection, cutSelection, pasteFromClipboard, removeSelection, shouldIgnoreForEditable, marketplaceVisible, selectedWidgets, dispatch, recipeId]);

  return null;
};

export default GlobalShortcuts;


